// The bets this browser placed, per owner: what each was for (the payout that cashes it out), the
// position it became and how it ended. The venue's account stream is the truth for money; this
// book only adds the bet's intent and lets the table cash out at the target or the clock.

import { useEffect } from "react";
import type { Account, Hex, Valuation } from "../sdk";
import { accountState, onAccountEvents, useOwner } from "../state/account";
import { closePositions, openPosition } from "../state/actions";
import { symbols } from "../state/market";
import { valuations } from "../state/valuation";
import { createStore, useStore } from "../state/store";
import { toast } from "../state/toasts";
import { big, paper, pnl, usd } from "../lib/num";
import { multiple } from "./tape";
import { paceName, type Call, type Clock, type Target } from "./slip";
import { celebrate } from "./celebrate";
import { sound } from "./sound";

export type Play = {
  intentId: Hex;
  coin: string;
  instrumentId: number;
  call: Call;
  /** Stake, raw collateral. */
  stake: string;
  leverage: number;
  target: Target;
  /** The move to the target, basis points from the entry. */
  targetBp: number | null;
  /** Mark at which the target pays, raw at the market's scale: from the quote, then from the entry. */
  targetMark: string | null;
  /** Payout at the target, raw collateral. */
  targetPayout: string | null;
  clock: Clock;
  placedAt: number;
  positionId: string | null;
  status: "sent" | "live" | "cashing" | "settled" | "void";
  /** Why the table cashed it out, when it did. */
  auto: "target" | "clock" | null;
  result: { netPnl: string; liquidated: boolean; at: number } | null;
};

type Book = Record<string, Play[]>;

const KEY = "hypa:book:v1";
const KEPT = 200;

function read(): Book {
  try {
    const text = localStorage.getItem(KEY);
    return text ? (JSON.parse(text) as Book) : {};
  } catch {
    return {};
  }
}

export const book = createStore<Book>(read());

book.subscribe(() => {
  try {
    localStorage.setItem(KEY, JSON.stringify(book.get()));
  } catch {
    // storage blocked: kept for this tab
  }
});

const EMPTY: Play[] = [];

export function usePlays(): Play[] {
  const owner = useOwner();
  return useStore(book, (all) => (owner ? (all[owner] ?? EMPTY) : EMPTY));
}

function update(owner: string, change: (plays: Play[]) => Play[]) {
  book.set((all) => {
    const before = all[owner] ?? EMPTY;
    const after = change(before);
    return after === before ? all : { ...all, [owner]: after.slice(0, KEPT) };
  });
}

function patchPlay(owner: string, test: (play: Play) => boolean, next: Partial<Play>) {
  update(owner, (plays) => {
    let changed = false;
    const out = plays.map((play) => {
      if (!test(play)) return play;
      changed = true;
      return { ...play, ...next };
    });
    return changed ? out : plays;
  });
}

export type Placing = Omit<Play, "intentId" | "placedAt" | "positionId" | "status" | "auto" | "result">;

export const playLabel = (play: Pick<Play, "call" | "coin" | "leverage" | "stake">) =>
  `${play.call === "up" ? "Up" : "Down"} ${play.coin} · ${paceName(play.leverage)} · ${usd(play.stake)}`;

/** Sends the play through the session key and books it. */
export async function placePlay(placing: Placing, stakeText: string): Promise<void> {
  const owner = accountState.get().owner;
  if (!owner) throw new Error("Connect a wallet first");
  const accepted = await openPosition(
    {
      instrumentId: placing.instrumentId,
      isLong: placing.call === "up",
      margin: stakeText,
      leverage: placing.leverage,
      useDebt: false,
      mark: null,
    },
    playLabel(placing),
  );
  const play: Play = {
    ...placing,
    intentId: accepted.intentId,
    placedAt: Date.now(),
    positionId: null,
    status: "sent",
    auto: null,
    result: null,
  };
  update(owner, (plays) => [play, ...plays]);
}

/** Cashes out one position now. */
export async function cashOut(positionId: string, label: string): Promise<void> {
  const owner = accountState.get().owner;
  if (!owner) return;
  patchPlay(owner, (play) => play.positionId === positionId && play.status === "live", { status: "cashing" });
  try {
    await closePositions([positionId], label);
  } catch (error) {
    patchPlay(owner, (play) => play.positionId === positionId && play.status === "cashing", { status: "live", auto: null });
    throw error;
  }
}

/** Links sent plays to the positions they opened, and settles the ones the account no longer holds. */
function reconcile(owner: string, account: Account) {
  const byIntent = new Map(account.positions.filter((p) => p.intentId).map((p) => [p.intentId?.toLowerCase(), p]));
  const open = new Set(account.positions.map((p) => p.positionId));
  const pending = new Set(account.operations.map((op) => op.intentId.toLowerCase()));
  const outcomes = new Map(account.outcomes.map(([id, outcome]) => [id.toLowerCase(), outcome]));
  update(owner, (plays) => {
    let changed = false;
    const out = plays.map((play): Play => {
      const id = play.intentId.toLowerCase();
      if (play.status === "sent") {
        const position = byIntent.get(id);
        if (position) {
          changed = true;
          const factor = play.targetBp === null ? null : BigInt(10_000 + (play.call === "up" ? play.targetBp : -play.targetBp));
          const targetMark = factor === null ? play.targetMark : ((big(position.entryPrice) * factor) / 10_000n).toString();
          return { ...play, positionId: position.positionId, status: "live", stake: position.margin, targetMark };
        }
        const outcome = outcomes.get(id);
        if (outcome && outcome !== "succeeded" && outcome !== "unknown") {
          changed = true;
          return { ...play, status: "void" };
        }
        // an hour without a position or a pending intent: it never opened
        if (!pending.has(id) && Date.now() - play.placedAt > 3_600_000) {
          changed = true;
          return { ...play, status: "void" };
        }
      }
      if ((play.status === "live" || play.status === "cashing") && play.positionId && !open.has(play.positionId) && account.applied === "snapshot") {
        // closed while this page was away: the stream's snapshot no longer holds it
        changed = true;
        return { ...play, status: "settled", result: play.result ?? null };
      }
      return play;
    });
    return changed ? out : plays;
  });
}

/** Cashes out a live play that reached its target or its clock: once each. */
function watch(owner: string, rows: Valuation[]) {
  const plays = book.get()[owner] ?? EMPTY;
  const byId = new Map(rows.map((row) => [row.positionId, row]));
  const now = Date.now();
  for (const play of plays) {
    if (play.status !== "live" || !play.positionId) continue;
    const valued = byId.get(play.positionId);
    if (!valued || valued.liquidated) continue;
    const hitTarget = play.targetPayout !== null && big(valued.payout) >= big(play.targetPayout);
    const hitClock = play.clock !== null && now - play.placedAt >= play.clock;
    if (!hitTarget && !hitClock) continue;
    const why = hitTarget ? "target" : "clock";
    patchPlay(owner, (held) => held.intentId === play.intentId, { auto: why });
    void cashOut(play.positionId, `${why === "target" ? "Target hit" : "Clock out"} · ${playLabel(play)}`).catch(() => undefined);
  }
}

/** Runs the book for the followed owner: mounted once by the app. */
export function useBookKeeper() {
  useEffect(() => {
    const onAccount = () => {
      const { owner, account } = accountState.get();
      if (owner && account) reconcile(owner, account);
    };
    const stopAccount = accountState.subscribe(onAccount);
    onAccount();

    const stopEvents = onAccountEvents(({ closed }) => {
      const owner = accountState.get().owner;
      if (!owner) return;
      for (const position of closed) {
        const play = (book.get()[owner] ?? EMPTY).find((held) => held.positionId === position.positionId);
        if (play) {
          patchPlay(owner, (held) => held.positionId === position.positionId, {
            status: "settled",
            result: { netPnl: position.netPnl, liquidated: position.liquidated, at: Date.now() },
          });
        }
        const what = play
          ? playLabel(play)
          : `${position.isLong ? "Up" : "Down"} ${symbols.get(position.instrumentId) ?? ""} · ${paceName(position.leverage)} · ${usd(position.margin)}`;
        const rake = big(position.paperMinted) > 0n ? ` · +${paper(position.paperMinted)} PAPER rakeback` : "";
        if (position.liquidated) {
          toast("down", "Busted", `${what} · ${pnl(position.netPnl)}${rake}`);
          celebrate("bust", pnl(position.netPnl), what, "0.00×");
          sound.bust();
        } else {
          const won = big(position.netPnl) > 0n;
          const title = play?.auto === "target" ? "Winner" : "Cashed out";
          toast(won ? "up" : "down", title, `${what} · ${pnl(position.netPnl)}${rake}`);
          if (won) {
            celebrate("win", pnl(position.netPnl), what, multiple(big(position.margin) + big(position.netPnl), big(position.margin)));
            sound.win();
          }
        }
      }
    });

    const check = () => {
      const owner = accountState.get().owner;
      const valued = valuations.get();
      if (owner && valued) watch(owner, valued.positions);
    };
    const stopValuations = valuations.subscribe(check);
    // clocks run out between marks too
    const timer = setInterval(check, 1_000);
    return () => {
      stopAccount();
      stopEvents();
      stopValuations();
      clearInterval(timer);
    };
  }, []);
}

export type Tally = { plays: number; wins: number; busts: number; net: bigint; best: bigint; streak: number; staked: bigint };

/** The record of settled plays with a known result, newest first. */
export function tally(plays: Play[]): Tally {
  const settled = plays.filter((play) => play.status === "settled" && play.result);
  let wins = 0;
  let busts = 0;
  let net = 0n;
  let best = 0n;
  let staked = 0n;
  for (const play of settled) {
    const result = big(play.result?.netPnl);
    net += result;
    staked += big(play.stake);
    if (result > best) best = result;
    if (result > 0n) wins += 1;
    if (play.result?.liquidated) busts += 1;
  }
  let streak = 0;
  for (const play of settled) {
    if (big(play.result?.netPnl) > 0n) streak += 1;
    else break;
  }
  return { plays: settled.length, wins, busts, net, best, streak, staked };
}
